import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown, Link as LinkIcon, ShieldAlert, EyeOff, TrendingDown, ShieldCheck, Brain, Banknote, Users, Lock, Shield, Server, Scan, Sparkles, Target, Palette, TrendingUp, Rocket, DollarSign, BarChart3, Building2, Globe, Handshake, Cpu, Database, Layers, Award, LogOut, Download } from 'lucide-react';
export function PitchDeck() {
  const [activeSlide, setActiveSlide] = useState(0);
  const totalSlides = 20;
  const containerRef = useRef<HTMLDivElement>(null);
  // Handle Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't interfere if user is typing in an input (though there are none here)
      if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) return;
      const isNext = ['ArrowDown', 'ArrowRight', ' '].includes(e.key);
      const isPrev = ['ArrowUp', 'ArrowLeft'].includes(e.key);
      if (isNext || isPrev) {
        e.preventDefault();
        const slides = document.querySelectorAll('.slide');
        let targetIndex = activeSlide;
        if (isNext && activeSlide < totalSlides - 1) {
          targetIndex = activeSlide + 1;
        } else if (isPrev && activeSlide > 0) {
          targetIndex = activeSlide - 1;
        }
        if (targetIndex !== activeSlide && slides[targetIndex]) {
          slides[targetIndex].scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown, {
      passive: false
    });
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeSlide, totalSlides]);
  // Handle Intersection Observer for active dot and animations
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const index = Number(entry.target.getAttribute('data-index'));
          setActiveSlide(index);
          entry.target.classList.add('is-visible');
        }
      });
    }, {
      threshold: 0.5
    });
    const slides = document.querySelectorAll('.slide');
    slides.forEach(slide => observer.observe(slide));
    return () => observer.disconnect();
  }, []);
  const scrollToSlide = (index: number) => {
    const slides = document.querySelectorAll('.slide');
    if (slides[index]) {
      slides[index].scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  return <div className="w-full h-full bg-[#060b18] text-[#f0f4ff] font-body" ref={containerRef} data-id="element-1">
      {/* Download PDF Button */}
      <button onClick={() => window.print()} className="print-hide fixed top-5 left-5 z-50 flex items-center gap-2 bg-[#111d35] border border-[#00c8ff]/20 text-[#7a8ba8] hover:text-[#00c8ff] hover:border-[#00c8ff]/40 hover:shadow-[0_0_15px_rgba(0,200,255,0.15)] px-4 py-2 rounded-full text-sm font-body transition-all duration-300" aria-label="Download as PDF" data-id="element-2">
        <Download className="w-4 h-4" data-id="element-3" />
        <span data-id="element-4">Download PDF</span>
      </button>

      {/* Navigation Dots */}
      <div className="nav-dots fixed right-6 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-2" data-id="element-5">
        {Array.from({
        length: totalSlides
      }).map((_, i) => <button key={i} onClick={() => scrollToSlide(i)} className={`w-2 h-2 rounded-full transition-all duration-300 ${activeSlide === i ? 'bg-[#00c8ff] scale-125 shadow-[0_0_10px_rgba(0,200,255,0.8)]' : 'bg-[#7a8ba8]/40 hover:bg-[#7a8ba8]/80'}`} aria-label={`Go to slide ${i + 1}`} data-id="element-6" />)}
      </div>

      {/* Slide 1: Title (data-index=0) */}
      <section className="slide" data-index="0" data-id="element-7">
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0" data-id="element-8">
          <div className="absolute top-1/4 left-1/4 w-[40vw] h-[40vw] max-w-lg max-h-lg bg-[#00c8ff] rounded-full mix-blend-screen filter blur-[100px] opacity-20 animate-pulse-glow" data-id="element-9"></div>
          <div className="absolute bottom-1/4 right-1/4 w-[40vw] h-[40vw] max-w-lg max-h-lg bg-[#6366f1] rounded-full mix-blend-screen filter blur-[100px] opacity-20 animate-pulse-glow" style={{
          animationDelay: '2s'
        }} data-id="element-10"></div>
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_70%,transparent_100%)]" data-id="element-11"></div>
        </div>

        <div className="slide-content text-center" data-id="element-12">
          <div className="mb-6 animate-on-view flex justify-center" data-id="element-13">
            <img src="https://cdn.magicpatterns.com/uploads/oUNBTi9b5rsXiX3m9xY58X/blob-b68f2d9.webp" alt="FIQIS Logo" className="w-28 h-28 md:w-36 md:h-36 object-contain" style={{
            filter: 'hue-rotate(80deg) saturate(1.5) brightness(1.4)',
            mixBlendMode: 'lighten'
          }} data-id="element-14" />
          </div>
          <h1 className="font-heading text-[length:var(--title-size)] text-gradient mb-4 animate-on-view tracking-tight" data-id="element-15">
            FIQIS
          </h1>
          <p className="text-[length:var(--h3-size)] text-[#f0f4ff] font-medium mb-6 animate-on-view delay-100" data-id="element-16">
            Financial Quantum Intelligence Systems
          </p>
          <p className="text-[#00c8ff] text-[length:var(--small-size)] uppercase tracking-widest mb-8 animate-on-view delay-200 font-bold" data-id="element-17">
            Venture-Capital Business Plan
          </p>
          <p className="text-[#7a8ba8] text-[length:var(--small-size)] animate-on-view delay-300" data-id="element-18">
            Arthur Miller — Founder & CEO{' '}
            <span className="mx-2 text-[#6366f1]" data-id="element-19">•</span> Mark Youwanes —
            Co-Founder & VP Engineering
          </p>
        </div>

        <div className="print-hide absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-on-view delay-400 text-[#7a8ba8]" data-id="element-20">
          <span className="text-sm uppercase tracking-widest" data-id="element-21">
            Scroll to explore
          </span>
          <ChevronDown className="w-5 h-5 animate-bounce" data-id="element-22" />
        </div>
      </section>

      {/* Slide 2: Executive Summary (data-index=1) */}
      <section className="slide" data-index="1" data-id="element-23">
        <div className="slide-content text-center w-full" data-id="element-24">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-6 animate-on-view" data-id="element-25">
            Executive Summary
          </h2>

          <p className="text-[length:var(--body-size)] text-[#f0f4ff] max-w-4xl mx-auto mb-10 animate-on-view delay-100 leading-relaxed" data-id="element-26">
            FIQIS is a financial infrastructure platform designed to help banks
            and credit unions modernize securely while retaining ownership of
            their customer relationships and revenue streams.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-10 w-full relative" data-id="element-27">
            <div className="hidden md:block absolute top-1/2 left-10 right-10 h-0.5 bg-gradient-to-r from-transparent via-[#00c8ff]/30 to-transparent -translate-y-1/2 z-0" data-id="element-28"></div>

            {[{
            icon: ShieldCheck,
            text: 'Post-Quantum Secure Integration'
          }, {
            icon: Brain,
            text: 'AI-Driven Financial Intelligence'
          }, {
            icon: Banknote,
            text: 'Bank-Owned Micro-Lending'
          }, {
            icon: Users,
            text: 'Regulated P2P Lending Networks'
          }].map((item, i) => <div key={i} className={`relative z-10 flex items-center gap-3 bg-[#0d1528] border border-[rgba(0,200,255,0.15)] rounded-full px-6 py-4 shadow-lg animate-on-view delay-${(i + 1) * 100} hover:border-[#00c8ff]/50 transition-colors`} data-id="element-29">
                <item.icon className="w-6 h-6 text-[#00c8ff]" data-id="element-30" />
                <span className="font-heading font-bold text-[length:var(--small-size)]" data-id="element-31">
                  {item.text}
                </span>
              </div>)}
          </div>

          <div className="bg-[#111d35]/50 border border-white/5 rounded-xl p-6 max-w-3xl mx-auto animate-on-view delay-400" data-id="element-32">
            <p className="text-[length:var(--body-size)] text-[#00c8ff] font-medium" data-id="element-33">
              Targeting 2,500+ institutions globally, generating over $1B in
              annual recurring revenue within ten years.
            </p>
          </div>
        </div>
      </section>

      {/* Slide 3: Industry Problem (data-index=2) */}
      <section className="slide" data-index="2" data-id="element-34">
        <div className="slide-content items-start w-full" data-id="element-35">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] animate-on-view" data-id="element-36">
            The Industry Problem
          </h2>

          <div className="grid-responsive w-full" data-id="element-37">
            {[{
            icon: LinkIcon,
            title: 'Integration Complexity',
            desc: 'Integrating new products with legacy banking cores takes 6–18 months and creates costly, fragile connections.'
          }, {
            icon: ShieldAlert,
            title: 'Security Evolution',
            desc: 'Quantum computing threatens traditional cryptographic systems. Institutions must prepare for post-quantum migration.'
          }, {
            icon: EyeOff,
            title: 'Data Underutilization',
            desc: 'Banks possess massive volumes of financial data but lack tools to convert it into meaningful insight.'
          }, {
            icon: TrendingDown,
            title: 'Revenue Loss to Fintech',
            desc: 'Companies like Klarna, Affirm, and Afterpay have captured significant portions of the consumer lending market.'
          }].map((item, i) => <div key={i} className={`bg-[#111d35] border-l-4 border-[#00c8ff] p-6 rounded-r-xl shadow-lg animate-on-view transition-transform hover:scale-[1.02] delay-${(i + 1) * 100}`} data-id="element-38">
                <item.icon className="w-8 h-8 text-[#00c8ff] mb-4" data-id="element-39" />
                <h3 className="font-heading text-[length:var(--h3-size)] mb-2" data-id="element-40">
                  {item.title}
                </h3>
                <p className="text-[length:var(--body-size)] text-[#7a8ba8]" data-id="element-41">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Slide 4: Market Opportunity (data-index=3) */}
      <section className="slide" data-index="3" data-id="element-42">
        <div className="slide-content items-start w-full" data-id="element-43">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] animate-on-view" data-id="element-44">
            Market Opportunity
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 w-full" data-id="element-45">
            {[{
            icon: Building2,
            stat: '4,500',
            desc: 'U.S. banks'
          }, {
            icon: Users,
            stat: '4,700',
            desc: 'U.S. credit unions'
          }, {
            icon: Globe,
            stat: '25K+',
            desc: 'Banks globally'
          }, {
            icon: Building2,
            stat: '85K+',
            desc: 'Global financial cooperatives and credit unions'
          }, {
            icon: Target,
            stat: '100K–120K',
            desc: 'Estimated serviceable institutions'
          }].map((item, i) => <div key={i} className={`bg-[#111d35] p-6 rounded-xl border border-white/5 shadow-lg animate-on-view transition-transform hover:scale-[1.02] delay-${(i + 1) * 100}`} data-id="element-46">
                <item.icon className="w-8 h-8 text-[#00c8ff] mb-4" data-id="element-47" />
                <h3 className="font-heading text-[length:var(--h2-size)] text-[#00c8ff] mb-2" data-id="element-48">
                  {item.stat}
                </h3>
                <p className="text-[length:var(--small-size)] text-[#7a8ba8]" data-id="element-49">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Slide 5: Market Drivers (data-index=4) */}
      <section className="slide" data-index="4" data-id="element-50">
        <div className="slide-content items-start w-full" data-id="element-51">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] animate-on-view" data-id="element-52">
            Market Drivers
          </h2>

          <div className="grid md:grid-cols-2 gap-[var(--content-gap)] w-full max-w-5xl mx-auto" data-id="element-53">
            {[{
            icon: TrendingUp,
            title: 'Digital Banking Transformation',
            desc: 'Banks must modernize to compete with fintech companies'
          }, {
            icon: ShieldCheck,
            title: 'Post-Quantum Cryptography',
            desc: 'NIST standardizing quantum-resistant algorithms — institutions must upgrade security'
          }, {
            icon: Brain,
            title: 'Data-Driven Services',
            desc: 'Customers increasingly expect personalized financial services'
          }, {
            icon: Banknote,
            title: 'Embedded Lending',
            desc: 'BNPL services have demonstrated strong consumer demand for flexible credit'
          }].map((item, i) => <div key={i} className={`bg-[#111d35] border-l-4 border-[#00c8ff] p-6 rounded-xl shadow-lg animate-on-view transition-transform hover:scale-[1.02] delay-${(i + 1) * 100}`} data-id="element-54">
                <item.icon className="w-8 h-8 text-[#00c8ff] mb-4" data-id="element-55" />
                <h3 className="font-heading text-[length:var(--h3-size)] mb-2" data-id="element-56">
                  {item.title}
                </h3>
                <p className="text-[length:var(--body-size)] text-[#7a8ba8]" data-id="element-57">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Slide 6: Product Platform (data-index=5) */}
      <section className="slide" data-index="5" data-id="element-58">
        <div className="slide-content text-center" data-id="element-59">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-4 animate-on-view" data-id="element-60">
            The FIQIS Platform
          </h2>
          <p className="text-[length:var(--h3-size)] text-[#7a8ba8] mb-[var(--content-gap)] animate-on-view delay-100" data-id="element-61">
            Four integrated capabilities delivered as white-label SaaS
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-[var(--content-gap)] w-full relative" data-id="element-62">
            <div className="hidden md:block absolute top-1/2 left-10 right-10 h-0.5 bg-gradient-to-r from-transparent via-[#00c8ff]/30 to-transparent -translate-y-1/2 z-0" data-id="element-63"></div>

            {[{
            icon: ShieldCheck,
            text: 'Post-Quantum Security'
          }, {
            icon: Brain,
            text: 'AI Intelligence'
          }, {
            icon: Banknote,
            text: 'Micro-Lending'
          }, {
            icon: Users,
            text: 'P2P Lending'
          }].map((item, i) => <div key={i} className={`relative z-10 flex items-center gap-3 bg-[#0d1528] border border-[rgba(0,200,255,0.15)] rounded-full px-6 py-4 shadow-lg animate-on-view delay-${(i + 1) * 100} hover:border-[#00c8ff]/50 transition-colors`} data-id="element-64">
                <item.icon className="w-6 h-6 text-[#00c8ff]" data-id="element-65" />
                <span className="font-heading font-bold text-[length:var(--body-size)]" data-id="element-66">
                  {item.text}
                </span>
              </div>)}
          </div>

          <p className="text-[length:var(--body-size)] text-[#f0f4ff] max-w-3xl mx-auto animate-on-view delay-400 bg-[#111d35]/50 p-6 rounded-xl border border-white/5" data-id="element-67">
            FIQIS enables financial institutions to deploy modern services
            within their own digital channels while maintaining regulatory
            compliance and brand ownership.
          </p>
        </div>
      </section>

      {/* Slide 7: Pillar 1 - Post-Quantum Security (data-index=6) */}
      <section className="slide" data-index="6" data-id="element-68">
        <div className="slide-content flex-row flex-wrap md:flex-nowrap items-center gap-[var(--content-gap)]" data-id="element-69">
          <div className="flex-1 w-full animate-on-view" data-id="element-70">
            <h2 className="font-heading text-[length:var(--h2-size)] mb-2" data-id="element-71">
              Pillar 1: Secure Integration Layer
            </h2>
            <p className="text-[length:var(--h3-size)] text-[#00c8ff] mb-8 font-heading" data-id="element-72">
              Post-Quantum Ready
            </p>

            <ul className="space-y-6" data-id="element-73">
              {[{
              icon: Lock,
              text: 'Standardized financial APIs'
            }, {
              icon: Shield,
              text: 'Secure integration gateway'
            }, {
              icon: Server,
              text: 'Fintech onboarding infrastructure'
            }, {
              icon: ShieldCheck,
              text: 'Post-quantum cryptographic readiness'
            }].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-74">
                  <div className="mt-1 bg-[#111d35] p-2 rounded-lg border border-[#00c8ff]/20" data-id="element-75">
                    <item.icon className="w-6 h-6 text-[#00c8ff]" data-id="element-76" />
                  </div>
                  <span className="text-[length:var(--body-size)] text-[#f0f4ff] leading-relaxed" data-id="element-77">
                    {item.text}
                  </span>
                </li>)}
            </ul>
          </div>

          <div className="flex-1 w-full flex justify-center animate-on-view delay-200" data-id="element-78">
            <div className="relative w-64 h-64 md:w-80 md:h-80" data-id="element-79">
              <div className="absolute inset-0 border border-[#00c8ff]/30 rotate-45 rounded-2xl animate-pulse-glow" data-id="element-80"></div>
              <div className="absolute inset-4 border border-[#6366f1]/40 rotate-[60deg] rounded-2xl animate-spin-slow" data-id="element-81"></div>
              <div className="absolute inset-8 border border-[#8b5cf6]/50 rotate-45 rounded-2xl bg-[#111d35]/80 backdrop-blur-md flex items-center justify-center shadow-[0_0_30px_rgba(0,200,255,0.2)]" data-id="element-82">
                <ShieldCheck className="w-16 h-16 text-[#00c8ff]" data-id="element-83" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 8: Pillar 2 - AI Intelligence (data-index=7) */}
      <section className="slide" data-index="7" data-id="element-84">
        <div className="slide-content flex-row flex-wrap md:flex-nowrap items-center gap-[var(--content-gap)]" data-id="element-85">
          <div className="flex-1 w-full flex justify-center order-2 md:order-1 animate-on-view delay-200" data-id="element-86">
            <div className="relative w-64 h-64 md:w-80 md:h-80" data-id="element-87">
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" data-id="element-88">
                <line x1="50" y1="20" x2="20" y2="70" stroke="rgba(0,200,255,0.2)" strokeWidth="1" data-id="element-89" />
                <line x1="50" y1="20" x2="80" y2="70" stroke="rgba(99,102,241,0.2)" strokeWidth="1" data-id="element-90" />
                <line x1="20" y1="70" x2="80" y2="70" stroke="rgba(139,92,246,0.2)" strokeWidth="1" data-id="element-91" />
              </svg>
              <div className="absolute top-[10%] left-1/2 -translate-x-1/2 w-16 h-16 rounded-full border border-[#00c8ff] bg-[#0d1528] flex items-center justify-center z-10 animate-float shadow-[0_0_15px_rgba(0,200,255,0.3)]" data-id="element-92">
                <Brain className="w-8 h-8 text-[#00c8ff]" data-id="element-93" />
              </div>
              <div className="absolute bottom-[20%] left-[10%] w-12 h-12 rounded-full border border-[#6366f1] bg-[#0d1528] flex items-center justify-center z-10 animate-float-delayed" data-id="element-94">
                <Scan className="w-6 h-6 text-[#6366f1]" data-id="element-95" />
              </div>
              <div className="absolute bottom-[20%] right-[10%] w-12 h-12 rounded-full border border-[#8b5cf6] bg-[#0d1528] flex items-center justify-center z-10 animate-float" data-id="element-96">
                <Target className="w-6 h-6 text-[#8b5cf6]" data-id="element-97" />
              </div>
            </div>
          </div>

          <div className="flex-1 w-full order-1 md:order-2 animate-on-view" data-id="element-98">
            <h2 className="font-heading text-[length:var(--h2-size)] mb-8" data-id="element-99">
              Pillar 2: AI-Driven Financial Intelligence
            </h2>
            <ul className="space-y-6" data-id="element-100">
              {[{
              icon: Scan,
              text: 'Customer cash-flow modeling'
            }, {
              icon: Sparkles,
              text: 'Behavioral financial insights'
            }, {
              icon: Brain,
              text: 'Explainable AI product recommendations'
            }, {
              icon: Target,
              text: 'Trust scoring models'
            }].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-101">
                  <div className="mt-1 bg-[#111d35] p-2 rounded-lg border border-[#6366f1]/20" data-id="element-102">
                    <item.icon className="w-6 h-6 text-[#6366f1]" data-id="element-103" />
                  </div>
                  <span className="text-[length:var(--body-size)] text-[#f0f4ff] leading-relaxed" data-id="element-104">
                    {item.text}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Slide 9: Pillar 3 - Micro-Lending (data-index=8) */}
      <section className="slide" data-index="8" data-id="element-105">
        <div className="slide-content flex-row flex-wrap md:flex-nowrap items-center gap-[var(--content-gap)]" data-id="element-106">
          <div className="flex-1 w-full animate-on-view" data-id="element-107">
            <h2 className="font-heading text-[length:var(--h2-size)] mb-8" data-id="element-108">
              Pillar 3: Micro-Lending Infrastructure
            </h2>

            <ul className="space-y-6 mb-8" data-id="element-109">
              {['Point-of-sale installment lending', 'Small balance consumer loans', 'In-app financing'].map((text, i) => <li key={i} className="flex items-start gap-4" data-id="element-110">
                  <div className="mt-1 bg-[#111d35] p-2 rounded-lg border border-[#00c8ff]/20" data-id="element-111">
                    <Banknote className="w-6 h-6 text-[#00c8ff]" data-id="element-112" />
                  </div>
                  <span className="text-[length:var(--body-size)] text-[#f0f4ff] leading-relaxed" data-id="element-113">
                    {text}
                  </span>
                </li>)}
            </ul>

            <div className="bg-[#111d35]/50 border-l-4 border-[#00c8ff] p-4 rounded-r-lg" data-id="element-114">
              <p className="text-[length:var(--body-size)] text-[#00c8ff]" data-id="element-115">
                Enables banks to reclaim revenue currently captured by fintech
                companies.
              </p>
            </div>
          </div>

          <div className="flex-1 w-full flex justify-center animate-on-view delay-200" data-id="element-116">
            <div className="relative w-64 h-64 md:w-80 md:h-80 flex items-center justify-center" data-id="element-117">
              <div className="absolute w-48 h-32 bg-[#111d35] border border-[#00c8ff]/30 rounded-xl transform -rotate-12 -translate-y-8 opacity-50 shadow-lg" data-id="element-118"></div>
              <div className="absolute w-48 h-32 bg-[#111d35] border border-[#6366f1]/40 rounded-xl transform -rotate-6 -translate-y-4 opacity-75 shadow-lg" data-id="element-119"></div>
              <div className="absolute w-48 h-32 bg-[#111d35] border border-[#8b5cf6]/50 rounded-xl flex items-center justify-center shadow-[0_0_30px_rgba(0,200,255,0.2)] backdrop-blur-sm z-10 animate-float" data-id="element-120">
                <Banknote className="w-16 h-16 text-[#00c8ff]" data-id="element-121" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 10: Pillar 4 - P2P Lending (data-index=9) */}
      <section className="slide" data-index="9" data-id="element-122">
        <div className="slide-content flex-row flex-wrap md:flex-nowrap items-center gap-[var(--content-gap)]" data-id="element-123">
          <div className="flex-1 w-full flex justify-center order-2 md:order-1 animate-on-view delay-200" data-id="element-124">
            <div className="relative w-64 h-64 md:w-80 md:h-80 flex items-center justify-center" data-id="element-125">
              <div className="absolute inset-0 border-2 border-dashed border-[#6366f1]/30 rounded-full animate-spin-slow" data-id="element-126"></div>
              <div className="absolute top-4 left-1/2 -translate-x-1/2 w-12 h-12 bg-[#111d35] border border-[#6366f1] rounded-full flex items-center justify-center shadow-lg" data-id="element-127">
                <Users className="w-6 h-6 text-[#6366f1]" data-id="element-128" />
              </div>
              <div className="absolute bottom-10 left-4 w-12 h-12 bg-[#111d35] border border-[#00c8ff] rounded-full flex items-center justify-center shadow-lg" data-id="element-129">
                <Users className="w-6 h-6 text-[#00c8ff]" data-id="element-130" />
              </div>
              <div className="absolute bottom-10 right-4 w-12 h-12 bg-[#111d35] border border-[#8b5cf6] rounded-full flex items-center justify-center shadow-lg" data-id="element-131">
                <Users className="w-6 h-6 text-[#8b5cf6]" data-id="element-132" />
              </div>
              <div className="w-20 h-20 bg-[#0d1528] border-2 border-[#6366f1] rounded-full flex items-center justify-center shadow-[0_0_20px_rgba(99,102,241,0.4)] z-10" data-id="element-133">
                <ShieldCheck className="w-10 h-10 text-[#6366f1]" data-id="element-134" />
              </div>
            </div>
          </div>

          <div className="flex-1 w-full order-1 md:order-2 animate-on-view" data-id="element-135">
            <h2 className="font-heading text-[length:var(--h2-size)] mb-2" data-id="element-136">
              Pillar 4: Peer-to-Peer Lending
            </h2>
            <p className="text-[length:var(--h3-size)] text-[#6366f1] mb-8 font-heading" data-id="element-137">
              Regulated lending within financial institutions
            </p>

            <ul className="space-y-6 mb-8" data-id="element-138">
              {[{
              icon: Shield,
              text: 'Regulatory oversight'
            }, {
              icon: Target,
              text: 'Risk management'
            }, {
              icon: Server,
              text: 'Loan servicing infrastructure'
            }].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-139">
                  <div className="mt-1 bg-[#111d35] p-2 rounded-lg border border-[#6366f1]/20" data-id="element-140">
                    <item.icon className="w-6 h-6 text-[#6366f1]" data-id="element-141" />
                  </div>
                  <span className="text-[length:var(--body-size)] text-[#f0f4ff] leading-relaxed" data-id="element-142">
                    {item.text}
                  </span>
                </li>)}
            </ul>

            <div className="bg-[#111d35]/50 border-l-4 border-[#6366f1] p-4 rounded-r-lg" data-id="element-143">
              <p className="text-[length:var(--body-size)] text-[#6366f1]" data-id="element-144">
                Creates an entirely new lending market within banking
                ecosystems.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 11: Technology Architecture (data-index=10) */}
      <section className="slide" data-index="10" data-id="element-145">
        <div className="slide-content items-start w-full" data-id="element-146">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-2 animate-on-view" data-id="element-147">
            Technology Architecture
          </h2>
          <p className="text-[length:var(--h3-size)] text-[#7a8ba8] mb-[var(--content-gap)] animate-on-view delay-100" data-id="element-148">
            Cloud-native platform built for enterprise reliability
          </p>

          <div className="grid md:grid-cols-2 gap-[var(--content-gap)] w-full max-w-5xl mx-auto mb-8" data-id="element-149">
            {[{
            icon: Server,
            title: 'Microservices Infrastructure'
          }, {
            icon: Layers,
            title: 'Event-Driven Data Pipelines'
          }, {
            icon: Brain,
            title: 'Distributed Analytics Engines'
          }, {
            icon: Lock,
            title: 'Secure Financial APIs'
          }].map((item, i) => <div key={i} className={`bg-[#111d35] border border-white/5 p-6 rounded-xl shadow-lg flex items-center gap-4 animate-on-view transition-transform hover:scale-[1.02] delay-${(i + 1) * 100}`} data-id="element-150">
                <div className="bg-[#0d1528] p-3 rounded-lg border border-[#00c8ff]/20" data-id="element-151">
                  <item.icon className="w-8 h-8 text-[#00c8ff]" data-id="element-152" />
                </div>
                <h3 className="font-heading text-[length:var(--h3-size)] text-white" data-id="element-153">
                  {item.title}
                </h3>
              </div>)}
          </div>

          <p className="text-center w-full text-[length:var(--small-size)] text-[#7a8ba8] animate-on-view delay-400" data-id="element-154">
            Deployed on AWS with enterprise-grade reliability
          </p>
        </div>
      </section>

      {/* Slide 12: Competitive Landscape (data-index=11) */}
      <section className="slide" data-index="11" data-id="element-155">
        <div className="slide-content items-start w-full" data-id="element-156">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] animate-on-view" data-id="element-157">
            Competitive Landscape
          </h2>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 w-full mb-[var(--content-gap)]" data-id="element-158">
            {[{
            name: 'Plaid',
            desc: 'Financial data connectivity'
          }, {
            name: 'nCino',
            desc: 'Banking software platform'
          }, {
            name: 'equipifi',
            desc: 'BNPL for credit unions'
          }, {
            name: 'Jifiti',
            desc: 'Embedded lending infrastructure'
          }].map((item, i) => <div key={i} className={`bg-[#111d35] p-6 rounded-xl border border-white/5 shadow-lg animate-on-view delay-${(i + 1) * 100}`} data-id="element-159">
                <h3 className="font-heading text-[length:var(--h3-size)] text-white mb-2" data-id="element-160">
                  {item.name}
                </h3>
                <p className="text-[length:var(--small-size)] text-[#7a8ba8]" data-id="element-161">
                  {item.desc}
                </p>
              </div>)}
          </div>

          <div className="w-full bg-[#111d35] rounded-xl p-8 border-gradient-top shadow-xl animate-on-view delay-400 text-center" data-id="element-162">
            <h3 className="font-heading text-[length:var(--h3-size)] text-gradient mb-4" data-id="element-163">
              The FIQIS Differentiation
            </h3>
            <p className="text-[length:var(--body-size)] text-[#f0f4ff] max-w-3xl mx-auto" data-id="element-164">
              FIQIS combines secure integration infrastructure, post-quantum
              readiness, AI-driven analytics, and lending infrastructure in a
              single unified platform.
            </p>
          </div>
        </div>
      </section>

      {/* Slide 13: Business Model (data-index=12) */}
      <section className="slide" data-index="12" data-id="element-165">
        <div className="slide-content w-full" data-id="element-166">
          <div className="text-center mb-[var(--content-gap)] animate-on-view" data-id="element-167">
            <h2 className="font-heading text-[length:var(--h2-size)] mb-2" data-id="element-168">
              Business Model
            </h2>
            <p className="text-[length:var(--body-size)] text-[#7a8ba8]" data-id="element-169">
              High-margin SaaS platform
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-[var(--content-gap)] w-full max-w-5xl mx-auto" data-id="element-170">
            <div className="bg-[#111d35] rounded-xl p-10 border-gradient-top shadow-xl animate-on-view delay-100 flex flex-col items-center text-center hover:scale-[1.02] transition-transform" data-id="element-171">
              <DollarSign className="w-12 h-12 text-[#00c8ff] mb-6" data-id="element-172" />
              <h3 className="font-heading text-[length:var(--h3-size)] mb-6 text-white" data-id="element-173">
                SaaS Licensing
              </h3>
              <div className="font-heading text-[length:var(--title-size)] text-gradient leading-none mb-4" data-id="element-174">
                $300,000
              </div>
              <p className="text-[length:var(--body-size)] text-[#7a8ba8]" data-id="element-175">
                per institution per year
              </p>
            </div>

            <div className="bg-[#111d35] rounded-xl p-10 border-gradient-top shadow-xl animate-on-view delay-200 flex flex-col items-center text-center hover:scale-[1.02] transition-transform" data-id="element-176">
              <BarChart3 className="w-12 h-12 text-[#6366f1] mb-6" data-id="element-177" />
              <h3 className="font-heading text-[length:var(--h3-size)] mb-6 text-white" data-id="element-178">
                Transaction Revenue
              </h3>
              <div className="font-heading text-[length:var(--title-size)] text-gradient leading-none mb-4" data-id="element-179">
                $100K–$300K
              </div>
              <p className="text-[length:var(--body-size)] text-[#7a8ba8]" data-id="element-180">
                additional per institution annually
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 14: Go-to-Market (data-index=13) */}
      <section className="slide" data-index="13" data-id="element-181">
        <div className="slide-content items-start w-full" data-id="element-182">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] animate-on-view" data-id="element-183">
            Go-to-Market Strategy
          </h2>

          <div className="grid md:grid-cols-2 gap-[var(--content-gap)] w-full max-w-5xl mx-auto" data-id="element-184">
            {[{
            icon: Handshake,
            title: 'Direct Sales',
            desc: 'Direct sales to financial institutions'
          }, {
            icon: Building2,
            title: 'Strategic Partnerships',
            desc: 'FIS, Fiserv, Jack Henry, digital banking providers'
          }, {
            icon: Users,
            title: 'Channel Partners',
            desc: 'Fintech consultants and banking technology integrators'
          }, {
            icon: Rocket,
            title: 'Pilot Programs',
            desc: 'Early adoption with discount incentives'
          }].map((item, i) => <div key={i} className={`bg-[#111d35] border-l-4 border-[#6366f1] p-6 rounded-xl shadow-lg animate-on-view transition-transform hover:scale-[1.02] delay-${(i + 1) * 100}`} data-id="element-185">
                <item.icon className="w-8 h-8 text-[#6366f1] mb-4" data-id="element-186" />
                <h3 className="font-heading text-[length:var(--h3-size)] mb-2" data-id="element-187">
                  {item.title}
                </h3>
                <p className="text-[length:var(--body-size)] text-[#7a8ba8]" data-id="element-188">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Slide 15: Growth & Revenue (data-index=14) */}
      <section className="slide" data-index="14" data-id="element-189">
        <div className="slide-content w-full" data-id="element-190">
          <div className="text-center mb-[var(--content-gap)] animate-on-view" data-id="element-191">
            <h2 className="font-heading text-[length:var(--h2-size)] mb-2" data-id="element-192">
              Growth Trajectory
            </h2>
            <p className="text-[length:var(--body-size)] text-[#7a8ba8]" data-id="element-193">
              10-Year Customer & Revenue Projection
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 w-full max-w-6xl mx-auto mb-12" data-id="element-194">
            {[{
            year: 'Year 1',
            rev: '$300K',
            inst: '1 institution'
          }, {
            year: 'Year 3',
            rev: '$8M',
            inst: '20 institutions'
          }, {
            year: 'Year 5',
            rev: '$80M',
            inst: '200 institutions'
          }, {
            year: 'Year 7',
            rev: '$360M',
            inst: '800 institutions'
          }, {
            year: 'Year 10',
            rev: '$1B+',
            inst: '2,500 institutions'
          }].map((item, i) => <div key={i} className={`bg-[#111d35] rounded-xl p-5 border-gradient-top shadow-lg animate-on-view delay-${(i + 1) * 100}`} data-id="element-195">
                <p className="text-[length:var(--small-size)] text-[#7a8ba8] mb-2 uppercase tracking-wider" data-id="element-196">
                  {item.year}
                </p>
                <h3 className="font-heading text-[length:var(--h3-size)] text-gradient mb-2" data-id="element-197">
                  {item.rev}
                </h3>
                <p className="text-[length:var(--small-size)] text-[#7a8ba8]" data-id="element-198">
                  {item.inst}
                </p>
              </div>)}
          </div>

          {/* CSS Growth Bar */}
          <div className="w-full max-w-6xl mx-auto relative h-12 animate-on-view delay-500" data-id="element-199">
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-[#00c8ff] to-[#6366f1] -translate-y-1/2 rounded-full" data-id="element-200"></div>
            <div className="absolute top-1/2 left-[0%] w-3 h-3 bg-[#00c8ff] rounded-full -translate-y-1/2 -translate-x-1/2 shadow-[0_0_10px_rgba(0,200,255,0.8)]" data-id="element-201"></div>
            <div className="absolute top-1/2 left-[25%] w-4 h-4 bg-[#21a6f6] rounded-full -translate-y-1/2 -translate-x-1/2 shadow-[0_0_10px_rgba(33,166,246,0.8)]" data-id="element-202"></div>
            <div className="absolute top-1/2 left-[50%] w-5 h-5 bg-[#4286ed] rounded-full -translate-y-1/2 -translate-x-1/2 shadow-[0_0_10px_rgba(66,134,237,0.8)]" data-id="element-203"></div>
            <div className="absolute top-1/2 left-[75%] w-6 h-6 bg-[#6366f1] rounded-full -translate-y-1/2 -translate-x-1/2 shadow-[0_0_15px_rgba(99,102,241,0.8)]" data-id="element-204"></div>
            <div className="absolute top-1/2 left-[100%] w-7 h-7 bg-[#8b5cf6] rounded-full -translate-y-1/2 -translate-x-1/2 shadow-[0_0_20px_rgba(139,92,246,0.8)]" data-id="element-205"></div>
          </div>
        </div>
      </section>

      {/* Slide 16: Funding Plan (data-index=15) */}
      <section className="slide" data-index="15" data-id="element-206">
        <div className="slide-content text-center" data-id="element-207">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-8 animate-on-view" data-id="element-208">
            Funding Plan
          </h2>

          <div className="mb-12 animate-on-view delay-100" data-id="element-209">
            <div className="font-heading text-[length:var(--title-size)] text-gradient leading-none mb-4" data-id="element-210">
              $5M
            </div>
            <div className="text-[#00c8ff] font-heading text-[length:var(--h3-size)]" data-id="element-211">
              Seed Round
            </div>
          </div>

          <div className="bg-[#111d35]/40 border border-white/5 rounded-2xl p-8 max-w-4xl mx-auto w-full animate-on-view delay-200" data-id="element-212">
            <p className="text-[length:var(--small-size)] text-[#7a8ba8] uppercase tracking-widest mb-6" data-id="element-213">
              Use of Funds
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-214">
              {[{
              icon: Server,
              label: 'Engineering Team'
            }, {
              icon: Brain,
              label: 'Platform Development'
            }, {
              icon: ShieldCheck,
              label: 'Security & Compliance'
            }, {
              icon: Users,
              label: 'Customer Onboarding & Sales'
            }].map((item, i) => <div key={i} className="flex flex-col items-center gap-3" data-id="element-215">
                  <div className="bg-[#111d35] p-4 rounded-full border border-white/5" data-id="element-216">
                    <item.icon className="w-6 h-6 text-[#6366f1]" data-id="element-217" />
                  </div>
                  <span className="text-[length:var(--small-size)] text-[#7a8ba8] text-center" data-id="element-218">
                    {item.label}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Slide 17: Exit Opportunities (data-index=16) */}
      <section className="slide" data-index="16" data-id="element-219">
        <div className="slide-content w-full" data-id="element-220">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] text-center animate-on-view" data-id="element-221">
            Exit Opportunities
          </h2>

          <div className="max-w-4xl mx-auto w-full space-y-6" data-id="element-222">
            <div className="bg-[#111d35]/40 border border-white/5 rounded-2xl p-8 text-center animate-on-view delay-100" data-id="element-223">
              <h3 className="font-heading text-[length:var(--h3-size)] text-white mb-6" data-id="element-224">
                Strategic Acquisition
              </h3>
              <div className="flex flex-wrap justify-center gap-4" data-id="element-225">
                {['Fiserv', 'FIS', 'Jack Henry', 'Visa', 'Mastercard'].map((company, i) => <div key={i} className="bg-[#0d1528] border border-[#00c8ff]/30 px-6 py-3 rounded-full text-[#f0f4ff] font-medium shadow-lg" data-id="element-226">
                      {company}
                    </div>)}
              </div>
            </div>

            <div className="bg-[#111d35]/40 border border-white/5 rounded-2xl p-8 text-center animate-on-view delay-200" data-id="element-227">
              <h3 className="font-heading text-[length:var(--h3-size)] text-white mb-4" data-id="element-228">
                Public Markets
              </h3>
              <p className="text-[length:var(--body-size)] text-[#7a8ba8]" data-id="element-229">
                IPO pathway once revenue exceeds{' '}
                <span className="text-[#00c8ff] font-bold" data-id="element-230">$500M ARR</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 18: Team (data-index=17) */}
      <section className="slide" data-index="17" data-id="element-231">
        <div className="slide-content w-full" data-id="element-232">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] text-center animate-on-view" data-id="element-233">
            Leadership
          </h2>

          <div className="grid md:grid-cols-2 gap-[var(--content-gap)] w-full max-w-5xl mx-auto" data-id="element-234">
            {/* Arthur */}
            <div className="bg-[#111d35] rounded-xl p-8 border border-white/5 animate-on-view delay-100 hover:scale-[1.02] transition-transform" data-id="element-235">
              <div className="flex items-center gap-4 mb-6" data-id="element-236">
                <div className="w-16 h-16 rounded-full bg-gradient-accent flex items-center justify-center text-xl font-heading font-bold text-white shadow-lg" data-id="element-237">
                  AM
                </div>
                <div data-id="element-238">
                  <h3 className="font-heading text-[length:var(--h3-size)] text-white" data-id="element-239">
                    Arthur Miller
                  </h3>
                  <p className="text-[#00c8ff] font-medium text-[length:var(--small-size)] uppercase tracking-wider" data-id="element-240">
                    Founder, CEO, CTO & CISO
                  </p>
                  <a href="https://www.linkedin.com/in/arthur-miller-64b32165/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[length:var(--small-size)] text-[#7a8ba8] hover:text-[#00c8ff] transition-colors mt-1" data-id="element-241">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" data-id="element-242">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" data-id="element-243" />
                    </svg>
                    LinkedIn
                  </a>
                </div>
              </div>
              <p className="text-[#f0f4ff] mb-6 text-[length:var(--body-size)] italic border-l-2 border-[#6366f1] pl-4" data-id="element-244">
                "Technology executive and enterprise architect with deep
                experience building secure financial infrastructure"
              </p>
              <ul className="space-y-3" data-id="element-245">
                {['Key architect of Zelle® and Real-Time Payments infrastructure', 'Former CTO of fintech BNPL platform for financial institutions', 'Engineering experience with U.S. Defense Information Systems Agency (DISA)'].map((text, i) => <li key={i} className="flex items-start gap-2 text-[#7a8ba8] text-[length:var(--small-size)]" data-id="element-246">
                    <span className="text-[#00c8ff] mt-1" data-id="element-247">•</span>
                    <span data-id="element-248">{text}</span>
                  </li>)}
              </ul>
            </div>

            {/* Mark */}
            <div className="bg-[#111d35] rounded-xl p-8 border border-white/5 animate-on-view delay-200 hover:scale-[1.02] transition-transform" data-id="element-249">
              <div className="flex items-center gap-4 mb-6" data-id="element-250">
                <div className="w-16 h-16 rounded-full bg-gradient-accent flex items-center justify-center text-xl font-heading font-bold text-white shadow-lg" data-id="element-251">
                  MY
                </div>
                <div data-id="element-252">
                  <h3 className="font-heading text-[length:var(--h3-size)] text-white" data-id="element-253">
                    Mark Youwanes
                  </h3>
                  <p className="text-[#00c8ff] font-medium text-[length:var(--small-size)] uppercase tracking-wider" data-id="element-254">
                    Co-Founder & VP Eng
                  </p>
                  <a href="https://www.linkedin.com/in/mark-youwanes-0b62898/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[length:var(--small-size)] text-[#7a8ba8] hover:text-[#00c8ff] transition-colors mt-1" data-id="element-255">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" data-id="element-256">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" data-id="element-257" />
                    </svg>
                    LinkedIn
                  </a>
                </div>
              </div>
              <p className="text-[#f0f4ff] mb-6 text-[length:var(--body-size)] italic border-l-2 border-[#6366f1] pl-4" data-id="element-258">
                "Software engineering leader specializing in scalable
                distributed systems and cloud infrastructure"
              </p>
              <ul className="space-y-3" data-id="element-259">
                {['Leads platform engineering and technology execution at FIQIS', 'Expert in cloud-native and microservice architectures', 'Deep experience in secure API and distributed systems'].map((text, i) => <li key={i} className="flex items-start gap-2 text-[#7a8ba8] text-[length:var(--small-size)]" data-id="element-260">
                    <span className="text-[#00c8ff] mt-1" data-id="element-261">•</span>
                    <span data-id="element-262">{text}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 19: Long-Term Vision (data-index=18) */}
      <section className="slide" data-index="18" data-id="element-263">
        <div className="slide-content text-center w-full" data-id="element-264">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-12 animate-on-view" data-id="element-265">
            Long-Term Impact
          </h2>

          <div className="max-w-4xl mx-auto animate-on-view delay-100 mb-12" data-id="element-266">
            <p className="font-heading text-[length:var(--h2-size)] text-gradient leading-tight" data-id="element-267">
              "FIQIS aims to become a core infrastructure platform for financial
              institutions worldwide."
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6 mb-12 animate-on-view delay-200" data-id="element-268">
            {['Secure integration infrastructure', 'AI-driven intelligence', 'Modern lending capabilities'].map((text, i) => <div key={i} className="bg-[#111d35] border border-[#00c8ff]/20 px-6 py-3 rounded-full text-[#f0f4ff] text-[length:var(--body-size)] shadow-lg" data-id="element-269">
                {text}
              </div>)}
          </div>

          <p className="text-[length:var(--h3-size)] text-[#7a8ba8] animate-on-view delay-300 max-w-3xl mx-auto" data-id="element-270">
            Enabling financial institutions to compete effectively in the
            evolving digital financial ecosystem.
          </p>
        </div>
      </section>

      {/* Slide 20: CTA (data-index=19) */}
      <section className="slide" data-index="19" data-id="element-271">
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0" data-id="element-272">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vw] h-[60vw] max-w-2xl max-h-2xl bg-[#00c8ff] rounded-full mix-blend-screen filter blur-[120px] opacity-10 animate-pulse-glow" data-id="element-273"></div>
        </div>

        <div className="slide-content text-center" data-id="element-274">
          <h2 className="font-heading text-[length:var(--title-size)] mb-6 animate-on-view leading-tight max-w-4xl mx-auto" data-id="element-275">
            Let's Build the Future of Banking Together
          </h2>
          <p className="text-[length:var(--h3-size)] text-[#7a8ba8] mb-12 animate-on-view delay-100" data-id="element-276">
            Ready to modernize your institution's infrastructure?
          </p>

          <a href="mailto:arthur@fiqis.com" className="inline-block bg-gradient-accent text-white font-heading font-bold text-[length:var(--body-size)] px-8 py-4 rounded-full shadow-[0_0_20px_rgba(0,200,255,0.4)] hover:shadow-[0_0_30px_rgba(0,200,255,0.6)] hover:scale-105 transition-all duration-300 animate-on-view delay-200" data-id="element-277">
            arthur@fiqis.com
          </a>

          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-on-view delay-300 flex flex-col items-center gap-3" data-id="element-278">
            <img src="https://cdn.magicpatterns.com/uploads/oUNBTi9b5rsXiX3m9xY58X/blob-b68f2d9.webp" alt="FIQIS Logo" className="w-12 h-12 object-contain opacity-30" style={{
            filter: 'hue-rotate(80deg) saturate(1.5) brightness(1.4)',
            mixBlendMode: 'lighten'
          }} data-id="element-279" />
            <span className="font-heading text-2xl font-bold tracking-widest text-white/20" data-id="element-280">
              FIQIS
            </span>
          </div>
        </div>
      </section>
    </div>;
}